import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Heart,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Loader2,
  CheckCircle2,
  Camera,
  Image,
  BookOpen,
  Calendar,
  User,
  Lock,
  AlertTriangle,
  BookImage,
  ArrowLeft,
  ArrowRight,
  Download
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

// Selection limits
const MIN_SELECTION = 35;
const MAX_SELECTION = 45;

// Step labels
const STEPS = [
  { id: 1, title: 'Fotoğraf Seçimi', subtitle: `${MIN_SELECTION}-${MAX_SELECTION} fotoğraf seçin`, icon: Camera },
  { id: 2, title: 'Kapak Fotoğrafı', subtitle: 'Albüm kapağı için 1 fotoğraf', icon: BookOpen },
  { id: 3, title: 'Tablo Fotoğrafı', subtitle: 'Tablo baskısı için 1 fotoğraf', icon: Image },
  { id: 4, title: 'Albüm Bilgileri', subtitle: 'İsim ve tarih bilgileri', icon: User },
  { id: 5, title: 'Albüm Modeli', subtitle: 'Albüm şablonu seçimi', icon: BookImage },
];

// Durum etiketleri
const STATUS_LABELS = {
  pending: { label: 'Seçim Aşamasında', color: 'bg-blue-500', icon: Camera },
  confirmed: { label: 'Seçim Tamamlandı', color: 'bg-green-500', icon: CheckCircle2 },
  processing: { label: 'İşlemde', color: 'bg-amber-500', icon: Loader2 },
  printing: { label: 'Baskıda', color: 'bg-purple-500', icon: BookOpen },
  completed: { label: 'Tamamlandı', color: 'bg-green-600', icon: CheckCircle2 },
};

// Progressive image loader: önce thumbnail (hızlı, net olmayabilir), 
// arkada full resolution yüklenince üzerine geçiş yapılır.
function ProgressiveImage({ thumbSrc, fullSrc, alt }) {
  const [fullLoaded, setFullLoaded] = useState(false);
  // fullSrc değişince state'i sıfırla
  useEffect(() => { setFullLoaded(false); }, [fullSrc]);

  // Thumbnail ile full URL aynı ise direkt full göster
  const sameUrl = !thumbSrc || thumbSrc === fullSrc;
  // Tek bir img element gösteriyoruz: önce thumbnail (blur), full yüklenince src değişiyor.
  // Bu sayede layout shift olmuyor ve sol/sağ butonlar yerinde kalıyor.
  const displaySrc = fullLoaded || sameUrl ? fullSrc : thumbSrc;

  return (
    <div className="relative flex items-center justify-center">
      <img
        src={displaySrc}
        alt={alt}
        className="max-h-[70vh] max-w-[90vw] object-contain transition-all duration-300 select-none"
        style={{
          ...((!fullLoaded && !sameUrl) ? { filter: 'blur(8px)', transform: 'scale(1.02)' } : {}),
          WebkitUserDrag: 'none',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
        draggable={false}
      />
      {/* Arka planda full resolution preload */}
      {!sameUrl && !fullLoaded && (
        <img
          src={fullSrc}
          alt=""
          onLoad={() => setFullLoaded(true)}
          className="hidden"
          aria-hidden="true"
        />
      )}
      {/* Yükleniyor göstergesi (sadece thumbnail yokken) */}
      {sameUrl && !fullLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/70" />
        </div>
      )}
    </div>
  );
}

export default function GalleryPage() {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(null);
  const [activeSubfolder, setActiveSubfolder] = useState('');
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  
  // Current step (1-5)
  const [currentStep, setCurrentStep] = useState(1);
  
  // Special photo selections
  const [coverPhotoId, setCoverPhotoId] = useState(null);
  const [tablePhotoId, setTablePhotoId] = useState(null);
  const [tablePhotoIds, setTablePhotoIds] = useState(new Set());
  const [multipleTableEnabled, setMultipleTableEnabled] = useState(false);
  
  // Template preview
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Album details form
  const [albumDetails, setAlbumDetails] = useState({
    album_names: '',
    album_date: '',
    selected_template_id: '',
    customer_note: ''
  });
  
  // Album templates
  const [albumTemplates, setAlbumTemplates] = useState([]);
  
  // Fullscreen preview
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState('view'); // 'view', 'select', 'cover', 'table'
  
  // Confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch gallery data
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API}/api/gallery/${token}`);
        if (!response.ok) {
          throw new Error('Galeri bulunamadı');
        }
        const data = await response.json();
        setGallery(data);
        
        // Initialize selections
        const selected = new Set(
          data.photos.filter(p => p.is_selected).map(p => p.id)
        );
        setSelectedIds(selected);
        
        // Initialize special photos
        setCoverPhotoId(data.cover_photo_id);
        setTablePhotoId(data.table_photo_id);
        
        // Initialize album details with defaults
        setAlbumDetails({
          album_names: data.album_names || '',
          album_date: data.album_date || '',
          selected_template_id: data.selected_template_id || '',
          customer_note: ''
        });
        
        // Set album templates
        setAlbumTemplates(data.album_templates || []);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [token]);

  // Toggle photo selection (Step 1)
  const toggleSelection = async (photoId) => {
    if (gallery.selection_locked) return;
    
    const newSelected = new Set(selectedIds);
    
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    
    setSelectedIds(newSelected);
    
    // Save to backend
    setSaving(true);
    try {
      await fetch(`${API}/api/gallery/${token}/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photo_ids: [photoId],
          selected: newSelected.has(photoId)
        })
      });
    } catch (error) {
      toast.error('Seçim kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Select special photo (cover/table) - Step 2 & 3
  const selectSpecialPhoto = async (photoId, type) => {
    if (gallery.selection_locked) return;
    
    try {
      if (type === 'cover') {
        await fetch(`${API}/api/gallery/${token}/set-special-photo?photo_type=${type}&photo_id=${photoId}`, {
          method: 'POST'
        });
        setCoverPhotoId(photoId);
        toast.success('Kapak fotoğrafı seçildi');
      } else {
        // Table photo - handle multiple
        if (multipleTableEnabled) {
          const newSet = new Set(tablePhotoIds);
          if (newSet.has(photoId)) {
            newSet.delete(photoId);
            toast.success('Tablo fotoğrafı kaldırıldı');
          } else {
            newSet.add(photoId);
            toast.success(`Tablo fotoğrafı seçildi (${newSet.size} adet)`);
          }
          setTablePhotoIds(newSet);
          setTablePhotoId(newSet.size > 0 ? [...newSet][0] : null);
          // Save all table photo IDs
          await fetch(`${API}/api/gallery/${token}/set-special-photo?photo_type=table&photo_id=${[...newSet].join(',')}`, {
            method: 'POST'
          });
        } else {
          await fetch(`${API}/api/gallery/${token}/set-special-photo?photo_type=${type}&photo_id=${photoId}`, {
            method: 'POST'
          });
          setTablePhotoId(photoId);
          setTablePhotoIds(new Set([photoId]));
          toast.success('Tablo fotoğrafı seçildi');
        }
      }
    } catch (error) {
      toast.error('Fotoğraf seçilemedi');
    }
  };

  // Final confirmation
  const handleFinalConfirm = async () => {
    setConfirming(true);
    try {
      // Save album details first
      await fetch(`${API}/api/gallery/${token}/album-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...albumDetails,
          cover_photo_id: coverPhotoId,
          table_photo_id: tablePhotoId
        })
      });
      
      // Confirm selections
      const response = await fetch(`${API}/api/gallery/${token}/confirm`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Onaylama başarısız');
      
      toast.success('Seçimleriniz onaylandı! 🎉');
      setShowConfirmDialog(false);
      
      // Reload gallery
      const newData = await fetch(`${API}/api/gallery/${token}`).then(r => r.json());
      setGallery(newData);
      
    } catch (error) {
      toast.error(error.message || 'Onaylama başarısız');
    } finally {
      setConfirming(false);
    }
  };

  // Selection count warning dialog
  const [showSelectionWarning, setShowSelectionWarning] = useState(false);
  const [selectionWarningMessage, setSelectionWarningMessage] = useState('');

  // Step validation (soft for step 1)
  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedIds.size > 0;
      case 2:
        return coverPhotoId !== null;
      case 3:
        return multipleTableEnabled ? tablePhotoIds.size > 0 : tablePhotoId !== null;
      case 4:
        return albumDetails.album_names.trim() !== '' && albumDetails.album_date.trim() !== '';
      case 5:
        return albumTemplates.length === 0 || albumDetails.selected_template_id !== '';
      default:
        return true;
    }
  };

  // Get validation message
  const getValidationMessage = () => {
    switch (currentStep) {
      case 1:
        if (selectedIds.size === 0) return 'En az 1 fotoğraf seçmelisiniz';
        return null;
      case 2:
        return coverPhotoId ? null : 'Kapak fotoğrafı seçmelisiniz';
      case 3:
        return tablePhotoId ? null : 'Tablo fotoğrafı seçmelisiniz';
      case 4:
        if (!albumDetails.album_names.trim()) return 'İsim bilgisi girilmeli';
        if (!albumDetails.album_date.trim()) return 'Tarih bilgisi girilmeli';
        return null;
      case 5:
        if (albumTemplates.length > 0 && !albumDetails.selected_template_id) {
          return 'Albüm modeli seçmelisiniz';
        }
        return null;
      default:
        return null;
    }
  };

  // Navigate steps with soft warning for photo count
  const goToNextStep = () => {
    if (!canProceedToNextStep()) {
      toast.error(getValidationMessage());
      return;
    }
    
    // Soft warning for step 1 if outside 35-45 range
    if (currentStep === 1) {
      if (selectedIds.size < MIN_SELECTION) {
        setSelectionWarningMessage(`${selectedIds.size} fotoğraf seçtiniz. Önerilen minimum ${MIN_SELECTION} fotoğraftır. Yine de devam etmek istiyor musunuz?`);
        setShowSelectionWarning(true);
        return;
      }
      if (selectedIds.size > MAX_SELECTION) {
        setSelectionWarningMessage(`${selectedIds.size} fotoğraf seçtiniz. Önerilen maksimum ${MAX_SELECTION} fotoğraftır. Yine de devam etmek istiyor musunuz?`);
        setShowSelectionWarning(true);
        return;
      }
    }
    
    if (currentStep === 5) {
      setShowConfirmDialog(true);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  // Proceed after warning confirmation
  const proceedAfterWarning = () => {
    setShowSelectionWarning(false);
    if (currentStep === 5) {
      setShowConfirmDialog(true);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  // Download individual photo with custom name
  const downloadPhoto = async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const nameWithoutExt = (photo.original_name || 'photo').replace(/\.[^.]+$/, '');
      const fileName = `${nameWithoutExt}-weddingtakipcom.JPG`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${fileName} indiriliyor...`);
    } catch (err) {
      toast.error('İndirme başarısız oldu');
    }
  };

  const goToPrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Fullscreen navigation
  const allPhotos = gallery?.photos || [];
  const subfolders = gallery?.subfolders || [];
  const photos = activeSubfolder === '__main__'
    ? allPhotos.filter(p => !(p.subfolder || ''))
    : activeSubfolder
      ? allPhotos.filter(p => (p.subfolder || '') === activeSubfolder)
      : allPhotos;
  
  // Get photos based on current mode (for cover/table selection, show only selected photos)
  const getPreviewPhotos = useCallback(() => {
    if (previewMode === 'cover' || previewMode === 'table') {
      return photos.filter(p => selectedIds.has(p.id));
    }
    return photos;
  }, [previewMode, photos, selectedIds]);
  
  const openPreview = (photo, index, mode = 'view') => {
    setPreviewPhoto(photo);
    setPreviewMode(mode);
    // Calculate correct index based on mode
    if (mode === 'cover' || mode === 'table') {
      const filteredPhotos = photos.filter(p => selectedIds.has(p.id));
      const filteredIndex = filteredPhotos.findIndex(p => p.id === photo.id);
      setPreviewIndex(filteredIndex >= 0 ? filteredIndex : 0);
    } else {
      setPreviewIndex(index);
    }
  };

  const closePreview = () => {
    setPreviewPhoto(null);
    setPreviewMode('view');
  };

  const goNextPhoto = useCallback(() => {
    const previewPhotos = getPreviewPhotos();
    if (previewIndex < previewPhotos.length - 1) {
      setPreviewIndex(prev => prev + 1);
      setPreviewPhoto(previewPhotos[previewIndex + 1]);
    }
  }, [previewIndex, getPreviewPhotos]);

  const goPrevPhoto = useCallback(() => {
    const previewPhotos = getPreviewPhotos();
    if (previewIndex > 0) {
      setPreviewIndex(prev => prev - 1);
      setPreviewPhoto(previewPhotos[previewIndex - 1]);
    }
  }, [previewIndex, getPreviewPhotos]);

  // Swipe (touch + mouse drag) navigation
  const touchStartRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const SWIPE_THRESHOLD = 60; // px

  const handleSwipeStart = useCallback((clientX) => {
    touchStartRef.current = clientX;
    setSwipeOffset(0);
  }, []);

  const handleSwipeMove = useCallback((clientX) => {
    if (touchStartRef.current === null) return;
    const delta = clientX - touchStartRef.current;
    // Sınırlı görsel feedback (-100, +100)
    const clamped = Math.max(-120, Math.min(120, delta));
    setSwipeOffset(clamped);
  }, []);

  const handleSwipeEnd = useCallback(() => {
    if (touchStartRef.current === null) return;
    const delta = swipeOffset;
    touchStartRef.current = null;
    setSwipeOffset(0);
    if (delta <= -SWIPE_THRESHOLD) {
      goNextPhoto();
    } else if (delta >= SWIPE_THRESHOLD) {
      goPrevPhoto();
    }
  }, [swipeOffset, goNextPhoto, goPrevPhoto]);

  const onTouchStart = (e) => handleSwipeStart(e.touches[0].clientX);
  const onTouchMove = (e) => handleSwipeMove(e.touches[0].clientX);
  const onTouchEnd = () => handleSwipeEnd();
  const onMouseDown = (e) => handleSwipeStart(e.clientX);
  const onMouseMove = (e) => {
    if (touchStartRef.current !== null) handleSwipeMove(e.clientX);
  };
  const onMouseUp = () => handleSwipeEnd();
  const onMouseLeave = () => {
    if (touchStartRef.current !== null) handleSwipeEnd();
  };

  // Handle selection in fullscreen mode
  const handleFullscreenSelection = useCallback(() => {
    if (!previewPhoto || gallery.selection_locked) return;
    
    if (previewMode === 'select') {
      toggleSelection(previewPhoto.id);
    } else if (previewMode === 'cover') {
      selectSpecialPhoto(previewPhoto.id, 'cover');
    } else if (previewMode === 'table') {
      selectSpecialPhoto(previewPhoto.id, 'table');
    }
  }, [previewPhoto, previewMode, gallery?.selection_locked, toggleSelection, selectSpecialPhoto]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewPhoto) return;
      if (e.key === 'ArrowRight') goNextPhoto();
      if (e.key === 'ArrowLeft') goPrevPhoto();
      if (e.key === 'Escape') closePreview();
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFullscreenSelection();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewPhoto, goNextPhoto, goPrevPhoto, handleFullscreenSelection]);

  // Image preloading for smoother fullscreen navigation
  useEffect(() => {
    if (!previewPhoto) return;
    const previewPhotos = getPreviewPhotos();
    const preloadRange = 3;
    for (let offset = 1; offset <= preloadRange; offset++) {
      const nextIdx = previewIndex + offset;
      const prevIdx = previewIndex - offset;
      if (nextIdx < previewPhotos.length) {
        const p = previewPhotos[nextIdx];
        // Önce thumbnail (hızlı), sonra full
        const img = new window.Image(); img.src = p.preview_url || p.url;
      }
      if (prevIdx >= 0) {
        const p = previewPhotos[prevIdx];
        const img = new window.Image(); img.src = p.preview_url || p.url;
      }
    }
  }, [previewIndex, previewPhoto, getPreviewPhotos]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-gray-400">Galeri yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Hata</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const status = gallery.status || 'pending';
  const isLocked = gallery.selection_locked;
  const StatusIcon = STATUS_LABELS[status]?.icon || Camera;

  // If gallery is locked (already confirmed)
  if (isLocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center py-8">
            <h1 className="text-3xl font-bold text-white mb-2">{gallery.customer_name}</h1>
            <p className="text-gray-400">{gallery.album_name}</p>
            
            {/* Status Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${STATUS_LABELS[status]?.color} text-white mt-4`}>
              <StatusIcon className="h-5 w-5" />
              {STATUS_LABELS[status]?.label}
            </div>
          </div>
          
          {/* Locked Message */}
          <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-6 text-center mb-4">
            <Lock className="h-12 w-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Seçim Tamamlandı</h2>
            <p className="text-gray-300">
              Albümünüz işleme alındığı için sadece fotoğrafları görüntüleyebilirsiniz.<br/>
              Değişiklik yapamazsınız.
            </p>
          </div>

          {/* Cleanup Warning */}
          {gallery.cleanup_scheduled_at && !gallery.cleanup_done && (
            <div className="bg-red-500/15 border border-red-500/50 rounded-xl p-4 text-center mb-8">
              <p className="text-red-200 text-sm">
                🗑️ <b>Bilgilendirme:</b> Onay tarihinizden itibaren 1 hafta sonra seçilmemiş fotoğraflar sunucudan silinecektir.<br/>
                <span className="text-red-300 text-xs">
                  Silinme tarihi: {new Date(gallery.cleanup_scheduled_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              </p>
            </div>
          )}
          {gallery.cleanup_done && (
            <div className="bg-gray-500/15 border border-gray-500/50 rounded-xl p-4 text-center mb-8">
              <p className="text-gray-300 text-sm">
                ℹ️ Seçilmemiş fotoğraflar sunucudan silinmiştir. Yalnızca seçilen fotoğraflar arşivde saklıdır.
              </p>
            </div>
          )}
          
          {/* Show selected photos */}
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {photos.filter(p => p.is_selected).map((photo, index) => (
              <div
                key={photo.id}
                className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform"
                onClick={() => openPreview(photo, index)}
              >
                <img
                  src={photo.thumbnail_url || photo.url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main gallery UI with steps
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold text-white">{gallery.customer_name}</h1>
            <p className="text-gray-400 text-sm">{gallery.album_name}</p>
          </div>
          
          {/* Step Progress */}
          <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => isCompleted && setCurrentStep(step.id)}
                    className={`flex flex-col items-center p-2 rounded-lg transition-all min-w-[80px] ${
                      isActive 
                        ? 'bg-primary text-white scale-105' 
                        : isCompleted 
                          ? 'bg-green-600 text-white cursor-pointer hover:bg-green-500' 
                          : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    <div className="relative">
                      <StepIcon className="h-6 w-6" />
                      {isCompleted && (
                        <CheckCircle2 className="h-4 w-4 absolute -top-1 -right-1 text-white bg-green-600 rounded-full" />
                      )}
                    </div>
                    <span className="text-[10px] mt-1 font-medium">{step.id}. Adım</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <ChevronRight className={`h-4 w-4 mx-1 ${isCompleted ? 'text-green-500' : 'text-gray-600'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Step Title */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-white">{STEPS[currentStep - 1].title}</h2>
          <p className="text-gray-400">{STEPS[currentStep - 1].subtitle}</p>
        </div>

        {/* Step 1: Photo Selection */}
        {currentStep === 1 && (
          <div>
            {/* Selection Counter */}
            <div className="flex justify-center mb-4">
              <div className={`px-4 py-2 rounded-full text-white font-medium ${
                selectedIds.size >= MIN_SELECTION && selectedIds.size <= MAX_SELECTION
                  ? 'bg-green-600'
                  : selectedIds.size > MAX_SELECTION
                    ? 'bg-amber-600'
                    : 'bg-amber-600'
              }`}>
                {selectedIds.size} fotoğraf seçildi
                {selectedIds.size < MIN_SELECTION && (
                  <span className="ml-1 text-xs opacity-80">(önerilen min: {MIN_SELECTION})</span>
                )}
                {selectedIds.size > MAX_SELECTION && (
                  <span className="ml-1 text-xs opacity-80">(önerilen max: {MAX_SELECTION})</span>
                )}
                {selectedIds.size >= MIN_SELECTION && selectedIds.size <= MAX_SELECTION && (
                  <span className="ml-1 text-xs opacity-80">({MIN_SELECTION}-{MAX_SELECTION} arası)</span>
                )}
              </div>
            </div>
            
            <p className="text-center text-gray-400 text-sm mb-4">
              💡 Fotoğrafa tıklayarak büyütüp inceleyebilir, büyük görünümde de seçim yapabilirsiniz
            </p>

            {/* Alt-Klasör Filtresi */}
            {subfolders.length > 0 && (
              <div className="mb-4 bg-white/5 border border-white/10 rounded-lg p-2" data-testid="gallery-subfolder-tabs">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 px-1">📂 Dosya Seçin</p>
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => setActiveSubfolder('')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeSubfolder === '' ? 'bg-white text-black shadow-sm' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                    data-testid="gallery-tab-all"
                  >
                    📁 Tümü ({allPhotos.length})
                  </button>
                  <button
                    onClick={() => setActiveSubfolder('__main__')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeSubfolder === '__main__' ? 'bg-white text-black shadow-sm' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                    style={{ display: allPhotos.some(p => !p.subfolder) ? undefined : 'none' }}
                  >
                    📁 Ana Albüm ({allPhotos.filter(p => !p.subfolder).length})
                  </button>
                  {subfolders.map(sf => (
                    <button
                      key={sf}
                      onClick={() => setActiveSubfolder(sf)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeSubfolder === sf ? 'bg-white text-black shadow-sm' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                      data-testid={`gallery-tab-${sf}`}
                    >
                      📂 {sf} ({allPhotos.filter(p => p.subfolder === sf).length})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {photos.map((photo, index) => {
                const isSelected = selectedIds.has(photo.id);
                return (
                  <div
                    key={photo.id}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-green-500 scale-95' : 'hover:scale-105'
                    }`}
                    onClick={() => openPreview(photo, index, 'select')}
                  >
                    <img
                      src={photo.thumbnail_url || photo.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                  decoding="async"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] p-1 truncate">
                      {photo.original_name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Cover Photo Selection */}
        {currentStep === 2 && (
          <div>
            {coverPhotoId && (
              <div className="text-center mb-4">
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm">
                  ✓ Kapak fotoğrafı seçildi
                </span>
              </div>
            )}
            
            <p className="text-center text-gray-400 mb-2">
              Albüm kapağında kullanılacak fotoğrafı seçin
            </p>
            <p className="text-center text-gray-500 text-sm mb-4">
              💡 Fotoğrafa tıklayarak büyütüp inceleyebilirsiniz
            </p>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {photos.filter(p => selectedIds.has(p.id)).map((photo, index) => {
                const isSelected = coverPhotoId === photo.id;
                return (
                  <div
                    key={photo.id}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                      isSelected ? 'ring-4 ring-blue-500 scale-95' : 'hover:scale-105'
                    }`}
                    onClick={() => openPreview(photo, index, 'cover')}
                  >
                    <img
                      src={photo.thumbnail_url || photo.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                  decoding="async"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-blue-500/40 flex items-center justify-center">
                        <BookOpen className="h-10 w-10 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Table Photo Selection */}
        {currentStep === 3 && (
          <div>
            {/* Multiple table toggle */}
            <div className="flex justify-center mb-4">
              <label className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full cursor-pointer" data-testid="multiple-table-toggle">
                <input
                  type="checkbox"
                  checked={multipleTableEnabled}
                  onChange={(e) => {
                    setMultipleTableEnabled(e.target.checked);
                    if (!e.target.checked && tablePhotoIds.size > 1) {
                      const first = [...tablePhotoIds][0];
                      setTablePhotoIds(new Set([first]));
                      setTablePhotoId(first);
                    }
                  }}
                  className="w-4 h-4 rounded accent-purple-500"
                />
                <span className="text-white text-sm">1'den fazla tablo seçmek istiyorum</span>
              </label>
            </div>

            {(tablePhotoId || tablePhotoIds.size > 0) && (
              <div className="text-center mb-4">
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm">
                  {multipleTableEnabled
                    ? `✓ ${tablePhotoIds.size} tablo fotoğrafı seçildi`
                    : '✓ Tablo fotoğrafı seçildi'
                  }
                </span>
              </div>
            )}
            
            <p className="text-center text-gray-400 mb-2">
              Tablo baskısı için kullanılacak fotoğrafı seçin
            </p>
            <p className="text-center text-gray-500 text-sm mb-4">
              {multipleTableEnabled
                ? '💡 Birden fazla fotoğraf seçebilirsiniz. Fotoğrafa tıklayarak büyütüp seçim yapabilirsiniz'
                : '💡 Fotoğrafa tıklayarak büyütüp inceleyebilirsiniz'
              }
            </p>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {photos.filter(p => selectedIds.has(p.id)).map((photo, index) => {
                const isSelected = multipleTableEnabled ? tablePhotoIds.has(photo.id) : tablePhotoId === photo.id;
                return (
                  <div
                    key={photo.id}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                      isSelected ? 'ring-4 ring-purple-500 scale-95' : 'hover:scale-105'
                    }`}
                    onClick={() => openPreview(photo, index, 'table')}
                  >
                    <img
                      src={photo.thumbnail_url || photo.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                  decoding="async"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-purple-500/40 flex items-center justify-center">
                        <Image className="h-10 w-10 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Album Details */}
        {currentStep === 4 && (
          <div className="max-w-md mx-auto">
            <div className="bg-gray-800 rounded-xl p-6 space-y-6">
              {/* Warning if changed */}
              {(gallery.original_names && albumDetails.album_names && albumDetails.album_names !== gallery.original_names) && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-200">
                    <p className="font-medium">İsim değiştirildi</p>
                    <p className="text-xs">Orijinal: {gallery.original_names}</p>
                  </div>
                </div>
              )}
              
              {(gallery.original_date && albumDetails.album_date && albumDetails.album_date !== gallery.original_date) && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-200">
                    <p className="font-medium">Tarih değiştirildi</p>
                    <p className="text-xs">Orijinal: {gallery.original_date}</p>
                  </div>
                </div>
              )}
              
              {/* Names Input */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Albüm Kapağına Yazılacak İsimler
                </Label>
                <Input
                  value={albumDetails.album_names}
                  onChange={(e) => setAlbumDetails({...albumDetails, album_names: e.target.value})}
                  placeholder="Örn: Ayşe & Mehmet"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              
              {/* Date Input */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Albüm Kapağına Yazılacak Tarih
                </Label>
                <Input
                  value={albumDetails.album_date}
                  onChange={(e) => setAlbumDetails({...albumDetails, album_date: e.target.value})}
                  placeholder="Örn: 15 Haziran 2026"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              
              {/* Customer Note */}
              <div className="space-y-2">
                <Label className="text-white">Notunuz (Opsiyonel)</Label>
                <textarea
                  value={albumDetails.customer_note}
                  onChange={(e) => setAlbumDetails({...albumDetails, customer_note: e.target.value})}
                  placeholder="Fotoğrafçınıza iletmek istediğiniz notlar..."
                  className="w-full p-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 resize-none"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Album Template Selection */}
        {currentStep === 5 && (
          <div>
            {albumTemplates.length > 0 ? (
              <>
                {albumDetails.selected_template_id && (
                  <div className="text-center mb-4">
                    <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm">
                      ✓ Albüm modeli seçildi
                    </span>
                  </div>
                )}
                
                <p className="text-center text-gray-400 mb-6">
                  Albümünüz için bir model seçin
                </p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                  {albumTemplates.map((template) => {
                    const isSelected = albumDetails.selected_template_id === template.id;
                    return (
                      <div
                        key={template.id}
                        className={`relative rounded-xl overflow-hidden cursor-pointer transition-all ${
                          isSelected ? 'ring-4 ring-green-500 scale-95' : 'hover:scale-105'
                        }`}
                      >
                        <div className="aspect-square bg-gray-800" onClick={() => template.image_url && setPreviewTemplate(template)}>
                          {template.image_url ? (
                            <img
                              src={`${API}${template.image_url}`}
                              alt={template.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookImage className="h-12 w-12 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2 text-center" onClick={() => setAlbumDetails({...albumDetails, selected_template_id: template.id})}>
                          <p className="text-white text-sm font-medium truncate">{template.name}</p>
                          {template.description && (
                            <p className="text-gray-400 text-xs truncate">{template.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                            <Check className="h-5 w-5 text-white" />
                          </div>
                        )}
                        {/* Select button overlay */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAlbumDetails({...albumDetails, selected_template_id: template.id});
                          }}
                          className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium ${
                            isSelected ? 'bg-green-500 text-white' : 'bg-black/60 text-white hover:bg-green-500'
                          }`}
                        >
                          {isSelected ? '✓ Seçildi' : 'Seç'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BookImage className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Fotoğrafçınız henüz albüm şablonu eklememiş.</p>
                <p className="text-gray-500 text-sm mt-2">Bu adımı atlayarak devam edebilirsiniz.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur border-t border-gray-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevStep}
            disabled={currentStep === 1}
            className="border-gray-600 text-white hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <div className="text-center text-white">
            <span className="text-sm">{currentStep} / 5</span>
          </div>
          
          <Button
            onClick={goToNextStep}
            disabled={!canProceedToNextStep()}
            className={currentStep === 5 ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {currentStep === 5 ? (
              <>
                Onayla
                <Check className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                İleri
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Selection Count Warning Dialog */}
      {showSelectionWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="selection-warning-dialog">
          <div className="bg-gray-800 rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white">Fotoğraf Sayısı Uyarısı</h3>
            </div>
            <p className="text-gray-300 text-center mb-6">{selectionWarningMessage}</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSelectionWarning(false)}
                className="flex-1 border-gray-600 text-white hover:bg-gray-700"
              >
                Geri Dön
              </Button>
              <Button
                onClick={proceedAfterWarning}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="selection-warning-proceed-btn"
              >
                Devam Et
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-white text-center mb-4">
              Seçimlerinizi Onaylıyor musunuz?
            </h3>
            
            <div className="bg-gray-700 rounded-xl p-4 space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Seçilen Fotoğraf:</span>
                <span className="text-white font-medium">{selectedIds.size} adet</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Kapak Fotoğrafı:</span>
                <span className="text-white font-medium">{coverPhotoId ? '✓ Seçildi' : '✗ Seçilmedi'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tablo Fotoğrafı:</span>
                <span className="text-white font-medium">
                  {multipleTableEnabled && tablePhotoIds.size > 0
                    ? `✓ ${tablePhotoIds.size} adet seçildi`
                    : tablePhotoId ? '✓ Seçildi' : '✗ Seçilmedi'
                  }
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">İsimler:</span>
                <span className="text-white font-medium">{albumDetails.album_names || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tarih:</span>
                <span className="text-white font-medium">{albumDetails.album_date || '-'}</span>
              </div>
              {albumDetails.selected_template_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Albüm Modeli:</span>
                  <span className="text-white font-medium">✓ Seçildi</span>
                </div>
              )}
            </div>
            
            <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 mb-3">
              <p className="text-amber-200 text-sm text-center">
                ⚠️ Onayladıktan sonra değişiklik yapamazsınız!
              </p>
            </div>
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-6">
              <p className="text-red-200 text-xs text-center leading-relaxed">
                🗑️ Onayladıktan <b>1 hafta sonra</b> seçilmemiş fotoğraflar sunucudan silinecektir.
                Yalnızca seçtiğiniz fotoğraflar saklanır. Lütfen seçiminizi dikkatlice yapın.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                disabled={confirming}
              >
                İptal
              </Button>
              <Button
                onClick={handleFinalConfirm}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={confirming}
              >
                {confirming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Evet, Onaylıyorum
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Preview */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between p-4 bg-black/50">
            <div className="text-white text-sm">
              {previewIndex + 1} / {getPreviewPhotos().length}
              {previewPhoto.original_name && (
                <span className="ml-4 text-gray-400">{previewPhoto.original_name}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => downloadPhoto(previewPhoto)}
                className="text-white hover:text-green-400 transition-colors"
                title="Fotoğrafı İndir"
                data-testid="fullscreen-download-btn"
              >
                <Download className="h-7 w-7" />
              </button>
              <button
                onClick={closePreview}
                className="text-white hover:text-gray-300"
              >
                <X className="h-8 w-8" />
              </button>
            </div>
          </div>
          
          {/* Main image area */}
          <div
            className="flex-1 flex items-center justify-center relative overflow-hidden select-none"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
          >
            <button
              onClick={goPrevPhoto}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-[60] text-white hover:text-gray-300 bg-black/60 rounded-full p-2 disabled:opacity-30"
              disabled={previewIndex === 0}
              data-testid="gallery-prev-btn"
              aria-label="Önceki fotoğraf"
              style={{ pointerEvents: 'auto' }}
            >
              <ChevronLeft className="h-10 w-10" />
            </button>

            <div
              className="flex items-center justify-center pointer-events-none"
              style={{
                transform: `translateX(${swipeOffset}px)`,
                transition: touchStartRef.current === null ? 'transform 0.25s ease-out' : 'none',
                opacity: 1 - Math.min(0.3, Math.abs(swipeOffset) / 400),
              }}
            >
              <ProgressiveImage
                key={previewPhoto.id}
                thumbSrc={previewPhoto.thumbnail_url || previewPhoto.url}
                fullSrc={previewPhoto.preview_url || previewPhoto.url}
                alt=""
              />
            </div>

            <button
              onClick={goNextPhoto}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-[60] text-white hover:text-gray-300 bg-black/60 rounded-full p-2 disabled:opacity-30"
              disabled={previewIndex === getPreviewPhotos().length - 1}
              data-testid="gallery-next-btn"
              aria-label="Sonraki fotoğraf"
              style={{ pointerEvents: 'auto' }}
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          </div>
          
          {/* Bottom action bar */}
          <div className="p-4 bg-black/50">
            {/* Selection mode - Step 1 */}
            {previewMode === 'select' && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-gray-400 text-sm">
                  Seçili: {selectedIds.size} / {MIN_SELECTION}-{MAX_SELECTION}
                </p>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleFullscreenSelection}
                    className={selectedIds.has(previewPhoto.id) 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                    }
                    size="lg"
                  >
                    {selectedIds.has(previewPhoto.id) ? (
                      <>
                        <X className="h-5 w-5 mr-2" />
                        Seçimi Kaldır
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        Fotoğrafı Seç
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-gray-500 text-xs">
                  Klavye: ← → ile gezin, Space/Enter ile seçin, Esc ile kapatın
                </p>
              </div>
            )}
            
            {/* Cover photo mode - Step 2 */}
            {previewMode === 'cover' && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-gray-400 text-sm">
                  Albüm kapağı için bir fotoğraf seçin
                </p>
                <Button
                  onClick={handleFullscreenSelection}
                  className={coverPhotoId === previewPhoto.id 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-blue-500 hover:bg-blue-600'
                  }
                  size="lg"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  {coverPhotoId === previewPhoto.id ? 'Kapak Olarak Seçildi ✓' : 'Kapak Fotoğrafı Olarak Seç'}
                </Button>
                <p className="text-gray-500 text-xs">
                  Klavye: ← → ile gezin, Space/Enter ile seçin
                </p>
              </div>
            )}
            
            {/* Table photo mode - Step 3 */}
            {previewMode === 'table' && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-gray-400 text-sm">
                  {multipleTableEnabled
                    ? `Tablo baskısı için fotoğraf seçin (${tablePhotoIds.size} seçildi)`
                    : 'Tablo baskısı için bir fotoğraf seçin'
                  }
                </p>
                <Button
                  onClick={handleFullscreenSelection}
                  className={(multipleTableEnabled ? tablePhotoIds.has(previewPhoto.id) : tablePhotoId === previewPhoto.id)
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'bg-purple-500 hover:bg-purple-600'
                  }
                  size="lg"
                >
                  <Image className="h-5 w-5 mr-2" />
                  {(multipleTableEnabled ? tablePhotoIds.has(previewPhoto.id) : tablePhotoId === previewPhoto.id)
                    ? 'Tablo Olarak Seçildi ✓'
                    : 'Tablo Fotoğrafı Olarak Seç'
                  }
                </Button>
                <p className="text-gray-500 text-xs">
                  Klavye: ← → ile gezin, Space/Enter ile seçin
                </p>
              </div>
            )}
            
            {/* View mode (locked gallery) */}
            {previewMode === 'view' && (
              <div className="text-center">
                <p className="text-gray-500 text-sm">
                  Klavye: ← → ile gezin, Esc ile kapatın
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setPreviewTemplate(null)} data-testid="template-preview-modal">
          <div className="relative max-w-3xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewTemplate(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={`${API}${previewTemplate.image_url}`}
              alt={previewTemplate.name}
              className="w-full h-full object-contain rounded-xl"
            />
            <div className="mt-3 text-center">
              <p className="text-white text-lg font-medium">{previewTemplate.name}</p>
              {previewTemplate.description && (
                <p className="text-gray-400 text-sm mt-1">{previewTemplate.description}</p>
              )}
              <Button
                onClick={() => {
                  setAlbumDetails({...albumDetails, selected_template_id: previewTemplate.id});
                  setPreviewTemplate(null);
                  toast.success('Albüm modeli seçildi');
                }}
                className="mt-3 bg-green-600 hover:bg-green-700"
                data-testid="template-select-from-preview"
              >
                <Check className="h-4 w-4 mr-2" />
                Bu Modeli Seç
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
